import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Route as RouteIcon, RotateCw, ExternalLink, PlugZap, Settings, History, KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { adoptSalesReporterSession, ensureSalesReporterSession, syncSalesReporterDirectory } from "@/lib/salesReporterSso";
import { toast } from "sonner";

// Sales Report — the live Sales Reporter app itself (Hostinger), served on the
// ERP's own origin by backend/sales_app_bridge.py at /api/sales-app/app/. Every
// screen and every record comes from its own MySQL database. With single
// sign-on set up (its existing Logbook secret pasted under Connection), the
// signed-in ERP employee opens it already signed in as their own Sales
// Reporter account (same email), which enforces every permission; otherwise
// they use the Sales Reporter's own login.

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";

function ConnectionDialog({ open, onOpenChange, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [check, setCheck] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setCheck(null);
    setReport(null);
    setSecret("");
    api.get("/sales-app/settings").then(({ data }) => { setCfg(data); setUrl(data.url); }).catch((e) => toast.error(errText(e)));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/sales-app/settings", secret ? { url, sso_secret: secret } : { url });
      setCfg(data);
      setSecret("");
      toast.success("Sales Reporter connection saved");
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const removeSecret = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/sales-app/settings", { url, sso_secret: "" });
      setCfg(data);
      toast.success("Single sign-on turned off");
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const runCheck = async () => {
    setBusy(true);
    try { setCheck((await api.get("/sales-app/sso/check")).data); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const runReport = async () => {
    setBusy(true);
    try { setReport((await api.get("/sales-app/sso/report")).data); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const readMembers = async () => {
    setBusy(true);
    const r = await syncSalesReporterDirectory({ force: true });
    if (r.ok) toast.success(`Member list read — ${r.members ?? "?"} members`); else toast.error(r.error);
    await runCheck();
  };
  const test = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/sales-app/settings/test");
      setResult({ ok: true, text: `The Sales Reporter answered — database ${data.database || "connected"}.` });
    } catch (e) { setResult({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sales-app-connection">
        <DialogHeader><DialogTitle>Connection to the Sales Reporter</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            The address of the Sales Reporter app on Hostinger. The ERP shows that app here with all of its data;
            people sign in with their own Sales Reporter accounts, so no password is stored in the ERP.
          </p>
          {cfg?.from_env?.length > 0 && <p className="text-xs text-amber-700">Set on the server (overrides this form): {cfg.from_env.join(", ")}</p>}
          <div className="space-y-1.5">
            <Label htmlFor="sales-app-url">Sales Reporter URL</Label>
            <Input id="sales-app-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-domain/salesreport/" data-testid="sales-app-cfg-url" />
          </div>
          <div className="space-y-1.5 border-t border-slate-100 pt-3">
            <Label htmlFor="sales-app-sso" className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Single sign-on secret</Label>
            <p className="text-xs text-slate-500">
              {cfg?.sso_configured ? "Set — employees open the Sales Reporter already signed in." : "Not set — employees sign in to the Sales Reporter themselves."}{" "}
              Paste the value of <code>$SSO_SHARED_SECRET</code> from the Sales Reporter's config.php (the one it already uses for Logbook) — nothing changes on Hostinger. It is never shown again after saving.
            </p>
            <Input id="sales-app-sso" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={cfg?.sso_configured ? "Leave blank to keep the current secret" : "Paste $SSO_SHARED_SECRET"}
              autoComplete="off" spellCheck={false} className="font-mono text-xs" data-testid="sales-app-cfg-sso" />
            {cfg?.sso_configured && !cfg?.from_env?.includes("SALES_APP_SSO_SECRET") && (
              <button type="button" className="text-xs text-red-600 hover:underline" onClick={removeSecret} disabled={busy}>Turn single sign-on off</button>
            )}
          </div>
          {cfg?.sso_configured && (
            <div className="flex gap-2 flex-wrap">
              <Button type="button" size="sm" variant="outline" onClick={runCheck} disabled={busy} data-testid="sales-app-sso-check">Check automatic sign-in</Button>
              <Button type="button" size="sm" variant="outline" onClick={readMembers} disabled={busy} data-testid="sales-app-sso-sync">Read member list now</Button>
              <Button type="button" size="sm" variant="outline" onClick={runReport} disabled={busy} data-testid="sales-app-sso-report">Who is signed in automatically</Button>
            </div>
          )}
          {report && (
            <div className="rounded-md border border-slate-200 bg-white text-xs max-h-72 overflow-auto" data-testid="sales-app-sso-report-result">
              <div className="px-3 py-2 border-b border-slate-100 text-slate-600 sticky top-0 bg-white">
                {report.users.filter((u) => u.ok).length} of {report.users.length} ERP users are signed in automatically · member list: {report.members} members
                {report.synced_at ? `, read ${new Date(report.synced_at * 1000).toLocaleString()}` : ", not read yet"}
              </div>
              <table className="w-full">
                <tbody>
                  {report.users.map((u) => (
                    <tr key={u.email || u.name} className="border-b border-slate-50 align-top">
                      <td className="px-3 py-1.5 w-5">{u.ok ? "✓" : "✗"}</td>
                      <td className="py-1.5 pr-2"><div className="font-medium text-slate-800">{u.name}</div><div className="text-slate-500">{u.email}</div></td>
                      <td className={`py-1.5 pr-3 ${u.ok ? "text-green-700" : "text-amber-800"}`}>{u.ok ? `as "${u.sales_reporter_name}"` : u.problem}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {report.sales_reporter_only?.length > 0 && (
                <div className="px-3 py-2 border-t border-slate-100 text-slate-600">
                  In the Sales Reporter but with no canaresai.com account of the same email: {report.sales_reporter_only.map((m) => `${m.name} (${m.email})`).join(", ")}
                </div>
              )}
            </div>
          )}
          {check && (
            <div className={`rounded-md border px-3 py-2 text-xs space-y-1 ${check.ok ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="sales-app-sso-check-result">
              <div className="font-semibold text-sm">{check.ok ? "Automatic sign-in will work for you." : check.problem}</div>
              {[
                ["allowed_sales_report", "Sales Report allowed for your ERP account"],
                ["secret_set", "Single sign-on secret saved"],
                ["member_list_read", `Member list read (${check.members} members${check.synced_at ? `, ${new Date(check.synced_at * 1000).toLocaleString()}` : ""})`],
                ["email_in_member_list", `Your email ${check.email || ""} is in the member list${check.sales_reporter_name ? ` as "${check.sales_reporter_name}"` : ""}`],
                ["account_active", "That Sales Reporter account is active"],
                ["name_unique", "No other account has the same name"],
              ].map(([k, t]) => <div key={k}>{check.checks?.[k] ? "✓" : "✗"} {t}</div>)}
            </div>
          )}
          {result && (
            <div className={`rounded-md border px-3 py-2 text-sm ${result.ok ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-700"}`} data-testid="sales-app-cfg-result">
              {result.text}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={test} disabled={busy || !cfg?.url} data-testid="sales-app-cfg-test">
            <PlugZap className="w-4 h-4 mr-1" /> Test connection
          </Button>
          <Button onClick={save} disabled={busy} data-testid="sales-app-cfg-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalesReporterApp() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [sso, setSso] = useState({ phase: "idle", message: "" });
  const [error, setError] = useState("");
  const [frameKey, setFrameKey] = useState(0);
  const [showConn, setShowConn] = useState(false);
  const appUrl = `${API_BASE}/sales-app/app/`;

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/sales-app/status");
      setStatus(data);
      setError("");
    } catch (e) { setError(errText(e)); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Sign the ERP user in to the Sales Reporter before its page loads, so it
  // opens straight into the app instead of its own login screen.
  const signIn = useCallback(async (opts) => {
    if (!status?.configured) return;
    if (!status.sso || !user) { setSso({ phase: "done", message: "" }); return; }
    setSso({ phase: "checking", message: "" });
    const r = await ensureSalesReporterSession(user, opts);
    setSso({ phase: "done", message: r.message || "" });
  }, [status, user]);
  useEffect(() => { signIn(); }, [signIn]);

  // Someone signed in with the Sales Reporter's own form inside the frame
  // (first time, or no automatic match): bind that session to this ERP user,
  // and if it's a GM/CEO, read the member list so everyone else can be signed
  // in automatically from now on.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "sr_token" || !e.newValue) return;
      adoptSalesReporterSession(user);
      syncSalesReporterDirectory({ force: true }).then((r) => { if (r.ok) loadStatus(); });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user, loadStatus]);
  const reload = async () => { await signIn(); setFrameKey((k) => k + 1); };

  return (
    <div className="flex flex-col gap-3 -m-2 md:-m-4 max-md:m-0 max-md:gap-0 max-md:flex-1 max-md:min-h-0" data-testid="sales-app-module">
      <div className="flex items-center justify-between gap-3 flex-wrap max-md:px-3 max-md:py-2">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
          <RouteIcon className="w-5 h-5 text-blue-600" /> Sales Report
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          {status?.configured && <>
            <Button size="sm" variant="outline" onClick={reload} data-testid="sales-app-reload">
              <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Reload
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="sales-app-open-full">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Full screen
              </a>
            </Button>
          </>}
          <Button size="sm" variant="ghost" asChild>
            <Link to="/sales-report/home" data-testid="sales-app-erp-entries">
              <History className="w-3.5 h-3.5 mr-1.5" /> Entries made in the ERP
            </Link>
          </Button>
          {status?.is_admin && (
            <Button size="sm" variant="outline" onClick={() => setShowConn(true)} data-testid="sales-app-open-connection">
              <Settings className="w-3.5 h-3.5 mr-1.5" /> Connection
            </Button>
          )}
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 max-md:mx-3 max-md:mb-2">{error}</div>}
      {status && !status.configured && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 max-md:mx-3 max-md:mb-2" data-testid="sales-app-not-configured">
          Sales Report isn't connected to the Sales Reporter yet{status.is_admin ? " — set its URL under Connection." : " — ask an admin to connect it."}
        </div>
      )}

      {sso.message && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 max-md:mx-3 max-md:mb-2" data-testid="sales-app-sso-message">
          {sso.message}
        </div>
      )}
      {status?.configured && sso.phase === "checking" && (
        <div className="w-full h-[calc(100vh-8.5rem)] min-h-[520px] max-md:h-auto max-md:flex-1 max-md:min-h-[320px] rounded-lg border border-slate-200 max-md:rounded-none max-md:border-x-0 max-md:border-b-0 bg-white flex items-center justify-center text-sm text-slate-500" data-testid="sales-app-signing-in">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing you in to the Sales Reporter…
        </div>
      )}
      {status?.configured && sso.phase === "done" && (
        <iframe
          key={frameKey}
          src={appUrl}
          title="Sales Reporter"
          // Visits capture the rep's GPS location.
          allow="geolocation"
          className="w-full h-[calc(100vh-8.5rem)] min-h-[520px] max-md:h-auto max-md:flex-1 max-md:min-h-[320px] rounded-lg border border-slate-200 max-md:rounded-none max-md:border-x-0 max-md:border-b-0 bg-white"
          // No allow-top-navigation: the Sales Reporter can never navigate the ERP away.
          sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-modals allow-popups allow-popups-to-escape-sandbox"
          data-testid="sales-app-frame"
        />
      )}

      {status?.is_admin && (
        <ConnectionDialog open={showConn} onOpenChange={setShowConn} onSaved={() => { loadStatus(); setFrameKey((k) => k + 1); }} />
      )}
    </div>
  );
}
